import type { CSSProperties, HTMLAttributes } from 'react';

import { cn } from '../utils/cn';

import { LogoInsolitum } from './LogoInsolitum';
import { SKALA_ZNAKU } from './geometria';

/**
 * Logo produktu — układ „INSOLITUM ∣ Universe": znak poziomy, kreska,
 * nazwa produktu tekstem interfejsu. Jedyny dopuszczony sposób postawienia
 * nazwy produktu przy znaku (zamiast słowa „INSOLITUM" wpisanego krojem
 * interfejsu obok sygnetu — to byłby fałszywy napis: prawdziwy jest krojem
 * Inter Display zamienionym na krzywe).
 *
 * Proporcje liczone od wysokości znaku H (`wysokosc`): odstępy 0,22 H (pole
 * ochronne), kreska × 0,5 H, nazwa Space Grotesk 500 w 0,53 H — wtedy
 * wysokość wersalików nazwy równa się wysokości liter napisu.
 *
 * Kreska ma kolor znaku z kryciem 0,4 (≈ 3:1 do tła, podporządkowana
 * napisowi) i grubość `max(1, round(H / 40))` px: przy H 45 jeden piksel,
 * przy H 80 dwa — inaczej na dużym logo robi się włosowa. Grubość liczy
 * komponent (zmienna `--ids-logo-kreska`), bo CSS nie zaokrągla do pikseli
 * we wszystkich przeglądarkach, a kreska na pół piksela się rozmywa.
 *
 * Domyślna wysokość to nagłówek ekranów wejścia (`SKALA_ZNAKU.naglowekWejscia`).
 *
 * Dla czytnika ekranu całość jest jednym obrazem „Insolitum {produkt}".
 * Komponent jest serwerowy.
 */
export interface LogoProduktuProps extends HTMLAttributes<HTMLSpanElement> {
  /** Nazwa produktu bez „INSOLITUM", np. „Universe", „Display", „Developers". */
  produkt: string;
  /** Wysokość znaku poziomego w pikselach. */
  wysokosc?: number;
}

/** Grubość kreski lockupu: cały piksel, rosnący z wysokością (H/40). */
export function gruboscKreski(wysokosc: number): number {
  return Math.max(1, Math.round(wysokosc / 40));
}

export function LogoProduktu({
  produkt,
  wysokosc = SKALA_ZNAKU.naglowekWejscia,
  className,
  style,
  ...props
}: LogoProduktuProps) {
  return (
    <span
      role="img"
      aria-label={`Insolitum ${produkt}`}
      className={cn('ids-logo-produktu', className)}
      style={
        {
          '--ids-logo-h': `${wysokosc}px`,
          '--ids-logo-kreska': `${gruboscKreski(wysokosc)}px`,
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      <LogoInsolitum uklad="poziome" tytul={null} className="ids-logo-produktu__znak" />
      <span className="ids-logo-produktu__kreska" aria-hidden />
      <span className="ids-logo-produktu__nazwa" aria-hidden>
        {produkt}
      </span>
    </span>
  );
}
